import { useState } from "react"
import {Menu, X, Calculator} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

const navLinks = [
  { label: "Gestation Calculator", href: "/#gestation-calculator" },
  { label: "Cattle Per Acre", href: "/cattle-per-acre-calculator" },
  { label: "Cow Calf Profit", href: "/cow-calf-profit-calculator" },
  { label: "Cost of Production", href: "/cow-calf-cost-of-production" },
  { label: "Tools", href: "/#tools" },
  { label: "About", href: "/#about" },
]

export default function Navbar() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const isHome = location.pathname === "/"

  const resolveHref = (href: string) => {
    // For hash links on the homepage, use simple anchors
    if (href.startsWith("/#") && isHome) return href.replace("/", "")
    return href
  }

  const isRouterLink = (href: string) => {
    return href.startsWith("/") && !href.startsWith("/#")
  }

  return (
    <nav className="sticky top-0 z-50 bg-[#283618]/95 backdrop-blur-md border-b border-[#40916C]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-lg bg-[#40916C] flex items-center justify-center group-hover:bg-[#2D6A4F] transition-colors">
              <Calculator className="w-5 h-5 text-[#FEFAE0]" />
            </div>
            <div className="flex flex-col">
              <span className="text-[#FEFAE0] font-bold text-base leading-tight tracking-tight" style={{ fontFamily: "'Merriweather', serif" }}>
                CowCalculator
              </span>
              <span className="text-[#B7E4C7] text-[10px] leading-tight tracking-widest uppercase">
                .com
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const href = resolveHref(link.href)
              if (isRouterLink(link.href)) {
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`px-4 py-2 text-sm rounded-lg transition-all duration-200 ${
                      location.pathname === link.href
                        ? "text-[#FEFAE0] bg-white/10"
                        : "text-[#B7E4C7] hover:text-[#FEFAE0] hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              }
              return (
                <a
                  key={link.href}
                  href={href}
                  className="px-4 py-2 text-sm text-[#B7E4C7] hover:text-[#FEFAE0] hover:bg-white/5 rounded-lg transition-all duration-200"
                >
                  {link.label}
                </a>
              )
            })}
          </div>

          {/* CTA */}
          <Link
            to={isHome ? "" : "/"}
            onClick={(e) => {
              if (isHome) {
                e.preventDefault()
                document.getElementById("gestation-calculator")?.scrollIntoView({ behavior: "smooth" })
              }
            }}
            className="hidden md:flex items-center gap-2 bg-[#D4A373] hover:bg-[#c4935f] text-[#283618] font-semibold text-sm px-5 py-2.5 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Calculate Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-[#FEFAE0] hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden bg-[#283618] border-t border-[#40916C]/20 animate-slide-down">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => {
              const href = resolveHref(link.href)
              if (isRouterLink(link.href)) {
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className={`block px-4 py-3 rounded-lg transition-colors text-base ${
                      location.pathname === link.href
                        ? "text-[#FEFAE0] bg-white/10"
                        : "text-[#B7E4C7] hover:text-[#FEFAE0] hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              }
              return (
                <a
                  key={link.href}
                  href={href}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-[#B7E4C7] hover:text-[#FEFAE0] hover:bg-white/5 rounded-lg transition-colors text-base"
                >
                  {link.label}
                </a>
              )
            })}
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="block mt-2 text-center bg-[#D4A373] hover:bg-[#c4935f] text-[#283618] font-semibold px-5 py-3 rounded-lg transition-colors"
            >
              Calculate Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}
